/**
 * ChatbotTurnList - renders the Platform Assistant conversation as per-turn blocks.
 *
 * Unlike the shared (flat, virtualized) SessionChatView, this groups each assistant turn as an optional
 * "Thought process" disclosure ABOVE a full answer body (chatbot-thinking-ux). The answer is always rendered
 * in full (no length-collapse); only the thinking collapses. The live (streaming) turn shows its disclosure
 * expanded; completed turns (latest and historical) show it collapsed — the live→completed transition is the
 * auto-collapse, so no explicit open-state juggling is needed.
 *
 * Reuses SessionMessage for message bubbles (answer body, user/system), ThoughtProcess for the disclosure,
 * and TypingIndicator for the pre-first-token wait. The orchestration session view keeps using
 * SessionChatView untouched.
 */
import { useEffect, useRef, useState, useCallback } from 'react'
import { ArrowDown } from 'lucide-react'
import { SessionMessage as SessionMessageComponent } from '@/components/sessions/SessionMessage'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { ChatbotMessage, ThoughtStep } from '@/types/chatbot'
import type { SessionMessage as SessionMessageType } from '@/lib/types'
import { ThoughtProcess } from './ThoughtProcess'
import { TypingIndicator } from './TypingIndicator'

/** The in-flight streaming turn (transient — not yet a persisted message). */
export interface LiveTurn {
  content: string
  reasoning?: string
  steps?: ThoughtStep[]
}

export interface ChatbotTurnListProps {
  /** Completed turns. Assistant messages may carry `reasoning`/`steps`/`thinkingSeconds`. */
  messages: ChatbotMessage[]
  /** The streaming turn, when one is in flight. */
  live?: LiveTurn | null
  /** True while a turn is being sent (drives the pre-first-token typing indicator). */
  isSending?: boolean
  className?: string
}

function toSessionMessage(msg: ChatbotMessage): SessionMessageType {
  return { role: msg.role, content: msg.content, timestamp: msg.timestamp }
}

/** An assistant turn: optional thought-process disclosure above the always-full answer body. */
function AssistantTurn({
  reasoning,
  steps,
  thinkingSeconds,
  answer,
  isLive,
}: {
  reasoning?: string
  steps?: ThoughtStep[]
  thinkingSeconds?: number
  answer?: SessionMessageType
  isLive?: boolean
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <ThoughtProcess reasoning={reasoning} steps={steps} elapsedSeconds={thinkingSeconds} isLive={isLive} />
      {answer && <SessionMessageComponent message={answer} disableCollapse />}
    </div>
  )
}

export function ChatbotTurnList({ messages, live, isSending = false, className }: ChatbotTurnListProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showScrollButton, setShowScrollButton] = useState(false)

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [])

  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100
    setShowScrollButton(!atBottom)
  }, [])

  // Auto-scroll to bottom as new content arrives, but only when the user is already near the bottom.
  const liveContentLen = live?.content?.length ?? 0
  const liveReasoningLen = live?.reasoning?.length ?? 0
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200
    if (nearBottom) scrollToBottom()
  }, [messages.length, liveContentLen, liveReasoningLen, scrollToBottom])

  const liveHasThinking = !!live && (!!live.reasoning || (live.steps?.length ?? 0) > 0)
  const showTypingIndicator = isSending && !liveHasThinking && !(live?.content)

  return (
    <div className={cn('relative h-full', className)}>
      <div ref={scrollRef} onScroll={handleScroll} className="h-full overflow-auto p-4 space-y-4" role="log" aria-label="Chat messages">
        {messages.map((msg, i) =>
          msg.role === 'assistant' ? (
            <AssistantTurn
              key={msg.id || i}
              reasoning={msg.reasoning}
              steps={msg.steps}
              thinkingSeconds={msg.thinkingSeconds}
              // A turn with only a thought process and no answer (e.g. a turn that errored mid-stream, TP7)
              // shows just the disclosure — no empty answer bubble.
              answer={msg.content ? toSessionMessage(msg) : undefined}
            />
          ) : (
            <SessionMessageComponent key={msg.id || i} message={toSessionMessage(msg)} />
          ),
        )}

        {/* In-flight streaming turn. */}
        {live && (liveHasThinking || live.content) && (
          <AssistantTurn
            reasoning={live.reasoning}
            steps={live.steps}
            answer={live.content ? { role: 'assistant', content: live.content } : undefined}
            isLive
          />
        )}

        {showTypingIndicator && <TypingIndicator label="Assistant is thinking…" />}
      </div>

      {showScrollButton && (
        <Button
          variant="secondary"
          size="icon"
          className="absolute bottom-4 right-4 rounded-full shadow-lg"
          onClick={scrollToBottom}
          aria-label="Scroll to bottom"
        >
          <ArrowDown className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
